import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEmbedNavigate } from '../../../hooks/useEmbedNavigate';
import Modal from 'react-modal';
import styles from './SummaryBlocks.module.scss';
import { MoodWidgetResponse, LeaderboardResponse } from '../../../api/Api';
import { useApiClient } from '../../../api/ApiClientContext';
import { Icon } from '../../../assets/icons/Icon';
import MoodSelectionPage from '../../../pages/moodSelectionPage/MoodSelectionPage';

interface RollingSummary {
  rolling_average: number | null;
  rolling_label: string | null;
  trend_direction: 'up' | 'down' | 'same' | 'no_data';
  current_period_average: number | null;
  previous_period_average: number | null;
}

const TREND_DIRECTION_MAP: Record<string, {
  icon: 'GreenIconsUpRight' | 'RedDownWithBGIcon' | 'NeutralWithBGIcon';
  text: string;
  bgClass: 'summaryBlocks__trendBgGreen' | 'summaryBlocks__trendBgRed' | 'summaryBlocks__trendBgNeutral';
}> = {
  up: { icon: 'GreenIconsUpRight', text: 'Better than last week', bgClass: 'summaryBlocks__trendBgGreen' },
  down: { icon: 'RedDownWithBGIcon', text: 'Lower than last week', bgClass: 'summaryBlocks__trendBgRed' },
  same: { icon: 'NeutralWithBGIcon', text: 'Same as last week', bgClass: 'summaryBlocks__trendBgNeutral' },
};

interface SummaryBlocksProps {
  moodWidget?: MoodWidgetResponse;
}

export const SummaryBlocks: React.FC<SummaryBlocksProps> = ({
  moodWidget,
}) => {
  const [isMoodModalOpen, setIsMoodModalOpen] = useState(false);
  const apiClient = useApiClient();
  const navigate = useEmbedNavigate();
  const queryClient = useQueryClient();

  const { data: leaderboardData } = useQuery<LeaderboardResponse>({
    queryKey: ['leaderboardSummary'],
    queryFn: async () => {
      const res = await apiClient.api.getLeaderboardApiV1SocialLeaderboardGet({ view_type: 'team' });
      return res?.data;
    },
  });

  const { data: userFlowStatus } = useQuery({
    queryKey: ['userFlowStatus'],
    queryFn: async () => {
      const res = await apiClient.api.getUserFlowStatusApiV1AuthUserFlowStatusGet();
      return res?.data;
    },
  });

  const needsMoodEntry = userFlowStatus?.needs_mood_entry === true;

  const rolling = (moodWidget?.rolling_summary as RollingSummary) ?? null;
  const trendInfo = rolling?.trend_direction ? TREND_DIRECTION_MAP[rolling.trend_direction] : null;

  const handleAddMood = () => setIsMoodModalOpen(true);

  const handleMoodSubmitted = () => {
    setIsMoodModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['userFlowStatus'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['moodHistory'] });
    queryClient.invalidateQueries({ queryKey: ['moodHistorySummary'] });
  };

  const teamTotalDays = leaderboardData?.leaderboard?.reduce((sum: number, m: any) => sum + (m.active_days ?? 0), 0) ?? 0;

  const achievementsData = { count: 3, thisWeek: 1 };

  const allAchievementIcons: Array<'Achievement1Icon' | 'Achievement2Icon' | 'Achievement3Icon' | 'Achievement4Icon'> = [
    'Achievement1Icon',
    'Achievement2Icon', 
    'Achievement3Icon',
    'Achievement4Icon',
  ];
  
  const today = new Date().toDateString();
  const seed = today.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const shuffled = [...allAchievementIcons].sort(() => (seed % 2) - 0.5);
  const displayedAchievementIcons = shuffled.slice(0, 3);

  return (
    <div className={styles.summaryBlocks}>
      {/* Daily Mood Block */}
      <div className={styles.summaryBlocks__card}>
        <div className={styles.summaryBlocks__header}>
          <span className={styles.summaryBlocks__title}>DAILY MOOD</span>
          <Icon iconName="SummaryMoodIcon" width="1.25rem" height="1.25rem" />
        </div>

        {needsMoodEntry ? (
          <>
            <div className={styles.summaryBlocks__content}>
              <span className={styles.summaryBlocks__notLogged}>
                You haven&apos;t logged your mood today
              </span>
            </div>
            <div className={styles.summaryBlocks__footer}>
              <button 
                className={styles.summaryBlocks__viewMore}
                onClick={() => navigate('/leaderboard')}
              >
                View more
              </button>
              <button
                className={styles.summaryBlocks__addMoodBtn}
                onClick={handleAddMood}
              >
                Add mood
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.summaryBlocks__content}>
              {rolling?.rolling_label ? (
                <>
                  <div className={styles.summaryBlocks__moodInfo}>
                    <span className={styles.summaryBlocks__moodText}>
                      This week: {rolling.rolling_label}
                    </span>
                    {trendInfo && (
                      <div className={styles[trendInfo.bgClass]}>
                        <Icon iconName={trendInfo.icon} width="1.5rem" height="1.5rem" />
                      </div>
                    )}
                  </div>
                  {trendInfo ? (
                    <span className={styles.summaryBlocks__subtitle}>{trendInfo.text}</span>
                  ) : null}
                </>
              ) : (
                <span className={styles.summaryBlocks__notLogged}>No mood data this week</span>
              )}
            </div>
            <div className={styles.summaryBlocks__footer}>
              <span />
              <button 
                className={styles.summaryBlocks__viewMore}
                onClick={() => navigate('/leaderboard')}
              >
                View more
              </button>
            </div>
          </>
        )}
      </div>

      {/* Achievements Block */}
      <div className={styles.summaryBlocks__card}>
        <div className={styles.summaryBlocks__header}>
          <span className={styles.summaryBlocks__title}>ACHIEVEMENTS</span>
            <Icon iconName="SummaryAchievementsIcon" width="1.25rem" height="1.25rem" />
      
        </div>
        <div className={styles.summaryBlocks__content}>
          <div className={styles.summaryBlocks__badgeStack}>
            {displayedAchievementIcons.map((iconName, index) => (
              <div key={iconName} className={styles.summaryBlocks__achievementBadge} style={{ zIndex: index + 1 }}>
                <Icon iconName={iconName} width="2.25rem" height="2.25rem" />
              </div>
            ))}
            <div className={styles.summaryBlocks__badgeCount}>+{achievementsData.count}</div>
          </div>
        </div>
        <div className={styles.summaryBlocks__footer}>
          <span className={styles.summaryBlocks__subtitle}>+{achievementsData.thisWeek} this week</span>
          <button 
            className={styles.summaryBlocks__viewMore} 
            onClick={() => navigate('/achievements')}
          >
            View more
          </button>
        </div>
      </div>

      {/* Team Activity Block */}
      <div className={styles.summaryBlocks__card}>
        <div className={styles.summaryBlocks__header}>
          <span className={styles.summaryBlocks__title}>My Team</span>
          <Icon iconName="TeamLeaderboardIcons" width="1.1rem" height="1.1rem" />
        </div>

        <div className={styles.summaryBlocks__footer}>
        <div className={styles.summaryBlocks__teamInfo}>
            <Icon iconName="RankIcon" width="43px" height="43px" />
            <div className={styles.summaryBlocks__rankInfo}>
              <span className={styles.summaryBlocks__rankLabel}>Your team logged</span>
              <span className={styles.summaryBlocks__rankValue}>{teamTotalDays} {teamTotalDays === 1 ? 'day' : 'days'} this week</span>
            </div>
          </div>
          <button
            className={styles.summaryBlocks__viewMore}
            onClick={() => navigate('/leaderboard?view=leaderboard')}
          >
            View more
          </button>
        </div>
      </div>

      <Modal
        isOpen={isMoodModalOpen}
        onRequestClose={() => setIsMoodModalOpen(false)}
        contentLabel="Log your mood"
        ariaHideApp={false}
        style={{
          overlay: { backgroundColor: 'rgba(16, 20, 48, 0.50)', zIndex: 1000 },
          content: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: 'none',
            borderRadius: 0,
            padding: 0,
          },
        }}
      >
        <button
          onClick={() => setIsMoodModalOpen(false)}
          className={styles.moodModalClose}
        >
          <Icon iconName="CrossIcon" width="1.5rem" height="1.5rem" />
        </button>
        <MoodSelectionPage
          onMoodSubmitted={handleMoodSubmitted}
          onClose={() => setIsMoodModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default SummaryBlocks;
